// Per-service-point KOT printer routing (Android kitchenId / split-kitchen
// printing). Each service point (kitchen station) can be assigned its own USB
// printer; a fired KOT is split by the line products' service points and each
// partial ticket prints on that station's printer. Lines without an assigned
// station printer go to the default receipt printer.

import { KotTicket } from '../types';
import { SavedPrinter, getSavedPrinter, printBytesTo, usbPrinterConfigured, printBytes } from './usbPrinter';
import { btPrinterConfigured, printBtBytes } from './btPrinter';
import { ethernetSelected, departmentIp, kotIp, printNetBytes } from './netPrinter';
import { buildKotEscPos } from './escpos';
import { getAppSettingsSync } from './db';
import { idbGetRaw, idbSetRaw } from './idb';
import { isOn } from './appSettings';

const KOT_PRINTERS_KEY = 'sconnect.kotPrinters';

// service-point NAME (matching Product.servicePoint) → assigned printer.
export type KotPrinterMap = Record<string, SavedPrinter>;

export async function getKotPrinterMap(): Promise<KotPrinterMap> {
  try {
    const raw = await idbGetRaw(KOT_PRINTERS_KEY);
    return raw ? (JSON.parse(raw) as KotPrinterMap) : {};
  } catch {
    return {};
  }
}

export async function setKotPrinter(servicePointName: string, printer: SavedPrinter | null): Promise<void> {
  const map = await getKotPrinterMap();
  if (printer) map[servicePointName] = printer;
  else delete map[servicePointName];
  await idbSetRaw(KOT_PRINTERS_KEY, JSON.stringify(map));
}

// Print a KOT, routed per service point. `servicePointOf` resolves a line's
// product to its service-point name (undefined → unrouted). Returns true when
// at least one partial ticket went out to a hardware printer (USB station
// printer, or the default USB/Bluetooth printer); false when nothing could be
// printed (caller decides on an HTML/browser fallback).
export async function printKotRouted(
  kot: KotTicket,
  storeName: string,
  servicePointOf: (productId: string) => string | undefined
): Promise<boolean> {
  // "12 — Service Point Setting / A - Multi Service Point Wise Product Print":
  // when off, the whole ticket goes to the default printer unsplit.
  const splitByStation = isOn(getAppSettingsSync(), 'multi_service_point_wise_product_print');

  // Ethernet printer type: route over TCP, per-department IP with the default
  // KOT IP as fallback (instead of the USB/Bluetooth transports below).
  if (ethernetSelected()) {
    return printKotEthernet(kot, storeName, servicePointOf, splitByStation);
  }

  const map = await getKotPrinterMap();

  // Group ticket lines by their station's assigned printer.
  const byStation = new Map<string, KotTicket['lines']>();
  const unrouted: KotTicket['lines'] = [];
  for (const line of kot.lines) {
    const sp = splitByStation ? servicePointOf(line.productId) : undefined;
    if (sp && map[sp]) {
      byStation.set(sp, [...(byStation.get(sp) ?? []), line]);
    } else {
      unrouted.push(line);
    }
  }

  let printed = false;

  for (const [station, lines] of Array.from(byStation.entries())) {
    const partial: KotTicket = { ...kot, lines, amount: lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0) };
    try {
      await printBytesTo(map[station], buildKotEscPos(partial, storeName, undefined, station));
      printed = true;
    } catch (e) {
      console.warn(`KOT print to station "${station}" failed:`, e);
      unrouted.push(...lines);
    }
  }

  // Remaining lines (no station printer / station failed) → default printer(s).
  // Print to BOTH a configured USB printer and a configured Bluetooth printer,
  // so the KOT comes out on whichever (or both) are set up. Each transport is
  // attempted independently; a failure on one does not block the other.
  if (unrouted.length > 0) {
    const rest: KotTicket = { ...kot, lines: unrouted, amount: unrouted.reduce((s, l) => s + l.quantity * l.unitPrice, 0) };
    const bytes = buildKotEscPos(rest, storeName);

    if (usbPrinterConfigured() && getSavedPrinter()) {
      try {
        await printBytes(bytes);
        printed = true;
      } catch (e) {
        console.warn('KOT print to default USB printer failed:', e);
      }
    }

    if (btPrinterConfigured()) {
      try {
        await printBtBytes(bytes);
        printed = true;
      } catch (e) {
        console.warn('KOT print to Bluetooth printer failed:', e);
      }
    }
  }

  return printed;
}

// Ethernet KOT routing: each line's service point maps to a department printer
// IP (Print Settings → Printer Selection → Department printer IPs); its partial
// ticket is streamed over TCP to that IP. Lines whose station has no IP (or no
// station) go to the default KOT printer IP. Returns true when at least one
// partial ticket reached a printer.
async function printKotEthernet(
  kot: KotTicket,
  storeName: string,
  servicePointOf: (productId: string) => string | undefined,
  splitByStation: boolean
): Promise<boolean> {
  const amountOf = (lines: KotTicket['lines']) =>
    lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);

  // Group lines by their station (only when that station has a configured IP).
  const byStation = new Map<string, KotTicket['lines']>();
  const unrouted: KotTicket['lines'] = [];
  for (const line of kot.lines) {
    const sp = splitByStation ? servicePointOf(line.productId) : undefined;
    if (sp && departmentIp(sp)) {
      byStation.set(sp, [...(byStation.get(sp) ?? []), line]);
    } else {
      unrouted.push(line);
    }
  }

  let printed = false;

  for (const [station, lines] of Array.from(byStation.entries())) {
    const ip = departmentIp(station)!;
    const partial: KotTicket = { ...kot, lines, amount: amountOf(lines) };
    try {
      await printNetBytes(ip, buildKotEscPos(partial, storeName, undefined, station));
      printed = true;
    } catch (e) {
      console.warn(`KOT print to station "${station}" (${ip}) failed:`, e);
      unrouted.push(...lines);
    }
  }

  // Remaining lines → default KOT printer IP.
  const fallbackIp = kotIp();
  if (unrouted.length > 0 && fallbackIp) {
    const rest: KotTicket = { ...kot, lines: unrouted, amount: amountOf(unrouted) };
    try {
      await printNetBytes(fallbackIp, buildKotEscPos(rest, storeName));
      printed = true;
    } catch (e) {
      console.warn(`KOT print to default KOT printer (${fallbackIp}) failed:`, e);
    }
  }

  return printed;
}
